import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Mail, Lock, User, Loader2, ArrowRight, Gift, AlertTriangle, Crown, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Google Icon component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const RegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { register, loginWithGoogle, googleAuthPending } = useAuth();
  const [loading, setLoading] = useState(false);
  const [startTrial, setStartTrial] = useState(true);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleTrialToggle = (checked) => {
    if (!checked) {
      setShowSkipModal(true);
    } else {
      setStartTrial(true);
    }
  };

  const confirmSkipTrial = () => {
    setStartTrial(false);
    setShowSkipModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.passwordsNoMatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name, startTrial);
      if (startTrial) {
        toast.success(t('auth.trialStarted'));
      } else {
        toast.success(t('auth.welcomeToMinnow'));
      }
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-ocean-blue flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)"/>
          </svg>
        </div>
        
        <div className="relative z-10 max-w-md text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-8">
              <GraduationCap className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              {t('auth.joinMinnow')}
            </h1>
            <p className="text-xl text-white/70">
              {t('auth.startJourneyDesc')}
            </p>
          </motion.div>
          
          {/* Floating elements */}
          <div className="absolute -top-10 -left-10 w-24 h-24 bg-mint rounded-2xl rotate-12 opacity-50 animate-float" />
          <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-coral rounded-2xl -rotate-12 opacity-50 animate-float" style={{ animationDelay: '1s' }} />
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl gradient-ocean flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-foreground">Minnow School</span>
            </Link>
          </div>

          <Card className="rounded-2xl shadow-xl border-0">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl">{t('auth.createAccount')}</CardTitle>
              <CardDescription>{t('auth.enterDetails')}</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Google Sign-In Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 rounded-xl mb-4 hover:bg-muted/50"
                onClick={handleGoogleSignIn}
                disabled={loading || googleAuthPending}
                data-testid="google-signup-btn"
              >
                {googleAuthPending ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <GoogleIcon />
                )}
                {t('auth.continueWithGoogle')}
              </Button>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('auth.orContinueWith')}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('auth.fullName')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 h-12 rounded-xl"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('common.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 h-12 rounded-xl"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('common.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 h-12 rounded-xl"
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="pl-10 h-12 rounded-xl"
                      required
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>

                {/* Trial Option */}
                <div className="p-4 rounded-xl bg-mint/20 border border-mint">
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      id="trial" 
                      checked={startTrial}
                      onCheckedChange={handleTrialToggle}
                      data-testid="trial-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="trial" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                        <Gift className="w-4 h-4 text-mint" />
                        {t('auth.startTrial')}
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('auth.trialDesc')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pricing Options - Always visible */}
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground text-center">
                    {startTrial ? 'Or skip the trial and subscribe now:' : 'Choose a plan to get started:'}
                  </p>
                  
                  {/* Basic Plan */}
                  <div className="p-3 rounded-xl border border-border hover:border-ocean-blue transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-ocean-blue" />
                        <span className="font-medium">Basic</span>
                        <Badge className="bg-ocean-blue text-xs">$29/mo</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">6 modules, 11 labs</span>
                    </div>
                  </div>
                  
                  {/* Pro Plan */}
                  <div className="p-3 rounded-xl border border-border hover:border-coral transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-coral" />
                        <span className="font-medium">Pro</span>
                        <Badge className="bg-coral text-xs">$99/mo</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">65 modules, all labs</span>
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground text-center">
                    You can upgrade anytime after signing up
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-xl gradient-ocean text-white"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('auth.creatingAccount')}
                    </>
                  ) : (
                    <>
                      {t('auth.createAccount')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">{t('auth.alreadyHaveAccount')} </span>
                <Link 
                  to="/login" 
                  className="text-ocean-blue hover:underline font-medium"
                  data-testid="register-login-link"
                >
                  {t('common.login')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Skip Trial Confirmation Modal */}
      <Dialog open={showSkipModal} onOpenChange={setShowSkipModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-coral" />
              {t('auth.skipTrialTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('auth.skipTrialDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="flex items-start gap-3 text-sm">
              <div className="w-5 h-5 rounded-full bg-coral/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-coral text-xs">✕</span>
              </div>
              <span className="text-muted-foreground">{t('auth.skipTrialNoModules')}</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <div className="w-5 h-5 rounded-full bg-coral/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-coral text-xs">✕</span>
              </div>
              <span className="text-muted-foreground">{t('auth.skipTrialNoLabs')}</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <div className="w-5 h-5 rounded-full bg-coral/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-coral text-xs">✕</span>
              </div>
              <span className="text-muted-foreground">{t('auth.skipTrialNoWeekly')}</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <div className="w-5 h-5 rounded-full bg-mint/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-mint text-xs">✓</span>
              </div>
              <span className="text-muted-foreground">{t('auth.skipTrialFreeModule')}</span>
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowSkipModal(false)} className="flex-1">
              {t('auth.keepTrial')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmSkipTrial}
              className="flex-1"
              data-testid="confirm-skip-trial-btn"
            >
              {t('auth.skipAnyway')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegisterPage;
